/** Pure helpers for the finance views: ranges, colours, labels and the model-mix what-if. */
import type { CostCategory, ModelMixRow, VarianceRow } from '@orgus/protocol';
import { COST_CATEGORIES, formatUsd } from '@orgus/protocol';
import { colourFor, formatDate } from '@orgus/ui';
import type { Tone } from '@orgus/ui';

/** Category series colours: the chart palette's slots in COST_CATEGORIES order, as the P&L chart draws them. */
export const CATEGORY_COLOR = Object.fromEntries(COST_CATEGORIES.map((c) => [c, colourFor(c, COST_CATEGORIES)])) as Record<CostCategory, string>;

export type RangeKey = '12w' | 'month' | 'last-month' | 'quarter';
export const RANGE_LABEL: Record<RangeKey, string> = { '12w': '12 weeks', month: 'This month', 'last-month': 'Last month', quarter: 'This quarter' };

const pad = (n: number) => String(n).padStart(2, '0');
export const dayOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** [from, to] days of a range, as of `now` (local time, like the hub). */
export function rangeFor(key: RangeKey, now: number): { from: string; to: string } {
  const d = new Date(now);
  const today = dayOf(d);
  if (key === 'month') return { from: dayOf(new Date(d.getFullYear(), d.getMonth(), 1)), to: today };
  if (key === 'last-month') return { from: dayOf(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: dayOf(new Date(d.getFullYear(), d.getMonth(), 0)) };
  if (key === 'quarter') return { from: dayOf(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)), to: today };
  const dow = (d.getDay() + 6) % 7;
  return { from: dayOf(new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow - 7 * 11)), to: today };
}

export function isRangeKey(v: string | null): v is RangeKey {
  return v === '12w' || v === 'month' || v === 'last-month' || v === 'quarter';
}

/** 'Oct 12' for a week starting that Monday. */
export function weekLabel(monday: string): string {
  const [y, m, d] = monday.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const dayTs = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).getTime();
};

/** 'Oct 14' for '2026-10-14' (the year only when it is not this one), through the shared formatter. */
export function dayText(day: string, now = Date.now()): string {
  return formatDate(dayTs(day), now);
}

/** A span of days, short: 'Oct 14', 'Oct 1 to 31', 'Sep 28 to Oct 3'. */
export function dayRangeText(from: string, to: string, now = Date.now()): string {
  if (from === to) return dayText(from, now);
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  return sameMonth ? `${dayText(from, now)} to ${Number(to.slice(8, 10))}` : `${dayText(from, now)} to ${dayText(to, now)}`;
}

/** 'Oct 2026' for '2026-10'. */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y!, m! - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** The last `n` months, newest first, as 'YYYY-MM'. */
export function recentMonths(now: number, n = 13): string[] {
  const d = new Date(now);
  return Array.from({ length: n }, (_, i) => {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    return `${m.getFullYear()}-${pad(m.getMonth() + 1)}`;
  });
}

/** Money or a dash-free "None" for a missing ratio. */
export const usdOrNone = (v: number | null, none = 'None yet') => (v === null ? none : formatUsd(v));

export function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 10) return `${h.toFixed(1)} h`;
  return `${Math.round(h).toLocaleString('en-US')} h`;
}

export function formatMinutes(m: number | null): string {
  if (m === null) return 'None';
  if (m < 1) return `${Math.max(1, Math.round(m * 60))} s`;
  if (m < 90) return `${m < 10 ? m.toFixed(1) : Math.round(m)} min`;
  return formatHours(m / 60);
}

/**
 * One decimal everywhere: '+5.3%', '-0.4%', '+11.0%'; a variance that is not zero but rounds
 * to it reads '<0.1%' (never '+0.0%'), exactly none reads '0.0%', no bill 'No bill'.
 */
export function formatVariancePct(v: number | null): string {
  if (v === null) return 'No bill';
  const pct = v * 100;
  if (pct !== 0 && Math.abs(pct) < 0.05) return '<0.1%';
  return `${pct > 0 ? '+' : pct < 0 ? '-' : ''}${Math.abs(pct).toFixed(1)}%`;
}

/** Variance rows are status, so they get a tone plus a label. */
export function varianceStatus(r: Pick<VarianceRow, 'flagged' | 'missing' | 'unmapped' | 'billing' | 'telemetry'>): { tone: Tone; label: string } {
  if (r.missing) return { tone: 'idle', label: 'Not billed yet' };
  if (r.unmapped) return { tone: 'neutral', label: 'No machines mapped' };
  if (r.flagged) return { tone: 'waiting', label: 'Over threshold' };
  if (r.billing === 0 && r.telemetry === 0) return { tone: 'idle', label: 'No spend' };
  return { tone: 'passed', label: 'Within threshold' };
}

/** A bar of the variance chart: one day, or a run of days on a narrow screen. */
export interface VarianceBucket {
  key: string;
  from: string;
  to: string;
  telemetry: number;
  billing: number;
  /** Over the billed days only; null when nothing in the bucket is billed. */
  variancePct: number | null;
  /** The billed days, first and last; null when none is billed. */
  billedFrom: string | null;
  billedTo: string | null;
  /** Every day in the bucket is billed, so Orgus and the bill cover the same days. */
  complete: boolean;
  flagged: boolean;
  /** No day in the bucket is billed yet. */
  missing: boolean;
}

/**
 * Days folded into buckets of `size` consecutive days (oldest first, the last bucket may be
 * shorter), so a phone draws a dozen readable bars instead of 90 hairlines.
 */
export function varianceBuckets(days: readonly VarianceRow[], size: number): VarianceBucket[] {
  const n = Math.max(1, Math.floor(size));
  const out: VarianceBucket[] = [];
  for (let i = 0; i < days.length; i += n) {
    const part = days.slice(i, i + n);
    const billed = part.filter((d) => !d.missing);
    const billing = billed.reduce((a, d) => a + d.billing, 0);
    const billedTelemetry = billed.reduce((a, d) => a + d.telemetry, 0);
    out.push({
      key: part[0]!.key,
      from: part[0]!.key,
      to: part[part.length - 1]!.key,
      telemetry: part.reduce((a, d) => a + d.telemetry, 0),
      billing,
      variancePct: billing > 0 ? (billedTelemetry - billing) / billing : null,
      flagged: part.some((d) => d.flagged),
      missing: billed.length === 0,
      billedFrom: billed[0]?.key ?? null,
      billedTo: billed[billed.length - 1]?.key ?? null,
      complete: billed.length === part.length,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// What if: route part of one model's work to another
// ---------------------------------------------------------------------------

export interface WhatIfMove {
  from: string;
  to: string;
  /** 0..1 of the from-model's tokens moved. */
  share: number;
}

/** Cost of some tokens at a model's rates (USD per million). */
export function costAt(t: ModelMixRow['tokens'], r: NonNullable<ModelMixRow['rates']>): number {
  const write5m = Math.max(0, t.cacheWrite - t.cacheWrite1h);
  return (t.input * r.input + t.output * r.output + t.cacheRead * r.cacheRead + write5m * r.cacheWrite + t.cacheWrite1h * r.cacheWrite1h) / 1e6;
}

/**
 * Savings if `share` of the from-model's tokens had run on the to-model, assuming the same
 * token counts (a different tokenizer or more retries change the real number). Uses the
 * current price list for both sides so the comparison is like for like.
 */
export function whatIf(mix: ModelMixRow[], move: WhatIfMove, rates: Record<string, NonNullable<ModelMixRow['rates']>>): { before: number; after: number; saved: number; savedShare: number } | null {
  const from = mix.find((m) => m.model === move.from);
  const fromRates = from?.rates ?? null;
  const toRates = rates[move.to] ?? mix.find((m) => m.model === move.to)?.rates ?? null;
  if (!from || !fromRates || !toRates) return null;
  const share = Math.min(1, Math.max(0, move.share));
  const moved = {
    input: from.tokens.input * share,
    output: from.tokens.output * share,
    cacheRead: from.tokens.cacheRead * share,
    cacheWrite: from.tokens.cacheWrite * share,
    cacheWrite1h: from.tokens.cacheWrite1h * share,
  };
  const before = costAt(moved, fromRates);
  const after = costAt(moved, toRates);
  const total = mix.reduce((a, m) => a + m.cost, 0);
  return { before, after, saved: before - after, savedShare: total > 0 ? (before - after) / total : 0 };
}

/** Where the CSV export downloads from (same origin; the session cookie signs it). */
export function financeExportUrl(o: { month: string; projectId: string | null; format: 'lines' | 'journal' }): string {
  const q = new URLSearchParams({ month: o.month, format: o.format });
  if (o.projectId) q.set('project', o.projectId);
  return `/api/finance/export.csv?${q.toString()}`;
}

/** Days between two 'YYYY-MM-DD' days, inclusive. */
export function daysInclusive(from: string, to: string): number {
  const [a, b] = [from, to].map((s) => {
    const [y, m, d] = s.split('-').map(Number);
    return Date.UTC(y!, m! - 1, d!);
  });
  return Math.round((b! - a!) / 86_400_000) + 1;
}
